import React, { useState, useEffect } from "react";
import Sidebar from "../components/Sidebar";
import Navbar from "../components/Navbar";
import API from "../utils/axiosInstance";
import { Document, Page } from "react-pdf";
import Modal from "react-modal";
import "./Ebook.css";
import "../pdfWorker";
import { useTranslation } from "react-i18next";

Modal.setAppElement('#root');

const backgroundColors = ["#91C5FF", "#F8CD81", "#C180F4", "#FA8282"];

const Ebooks = () => {
  const { t, i18n } = useTranslation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [ebooks, setEbooks] = useState([]);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState("");
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isAdmin, setIsAdmin] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [accessList, setAccessList] = useState([]);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user) {
      API.get(`/ebooks/user-access/${user.id}`).then(res => setAccessList(res.data));
    }
  }, []);

  const hasAccess = (ebookId) => accessList.includes(ebookId);

  useEffect(() => {
    fetchAllEbooks();
  }, []);

  const fetchAllEbooks = async () => {
    try {
      const res = await API.get("/ebooks/all");
      setEbooks(res.data);
    } catch (err) {
      console.error("❌ Error fetching ebooks:", err);
    }
  };

  const fetchPdfBlob = async (id) => {
    try {
      const res = await API.get(`/ebooks/stream/${id}`, {
        responseType: "blob",
      });
      return URL.createObjectURL(res.data);
    } catch (err) {
      console.error("❌ Failed to fetch PDF blob:", err);
      throw err;
    }
  };

  const initiatePayment = async (ebookId, price) => {
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      if (!user) {
        alert(t("LoginToPurchase"));
        return;
      }

      const params = {
        gateway: "paypal",
        accountId: 0,
        amount: price,
        currency: "USD",
        note: `Purchase Ebook ${ebookId}`,
        ebookId
      };

      const res = await API.post("/orders/create", { params, account: {}, customer: user });

      if (res.data.order?.link) {
        window.location.href = res.data.order.link;
      } else {
        alert(t("FailedToCreatePaymentLink"));
      }
    } catch (err) {
      console.error("❌ Payment initiation error:", err);
      alert(t("PaymentFailed"));
    }
  };

  const handleView = async (ebookId) => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (!user) {
      alert(t("LoginToAccess"));
      return;
    }

    try {
      const res = await API.get(`/ebooks/check-access/${ebookId}/${user.id}`);
      if (!res.data.access) {
        if (window.confirm(t("PurchaseToView"))) {
          initiatePayment(ebookId);
        }
        return;
      }

      const blobUrl = await fetchPdfBlob(ebookId);
      setPdfUrl(blobUrl);
      setCurrentPage(1);
      setModalIsOpen(true);
    } catch (err) {
      console.error("❌ Access check failed:", err);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();

    if (!file) {
      return alert(t("SelectFileWarning"));
    }

    const formData = new FormData();
    formData.append("title", title);
    formData.append("description", description);
    formData.append("ebook", file);

    try {
      setUploading(true);
      await API.post("/ebooks/upload", formData);
      alert(t("UploadSuccess"));
      setTitle("");
      setDescription("");
      setFile(null);
      setUploadModalOpen(false);
      fetchAllEbooks();
    } catch (error) {
      console.error("❌ Upload error:", error);
      alert(t("UploadFailed"));
    } finally {
      setUploading(false);
    }
  };

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  useEffect(() => {
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
  }, [pdfUrl]);

  return (
    <div
      className="dashboard-wrapper"
      dir={['ar', 'fa', 'ir', 'he'].includes(i18n.language) ? 'rtl' : 'ltr'}
      style={{
        flexDirection: ['ar', 'fa', 'ir', 'he'].includes(i18n.language)
          ? 'row-reverse'
          : 'row',
      }}
    >      <div className="sidebar-fixed">
        <Sidebar isOpen={isSidebarOpen} />
      </div>



      <div className="dashboard-main">
        <Navbar onToggleSidebar={() => setIsSidebarOpen((prev) => !prev)} />

        <div className="ebook-content">
          <div className="ebook-header">
            <h4 className="section-title">{t("MyEbooks")}</h4>
            {isAdmin && (
              <button onClick={() => setUploadModalOpen(true)} className="upload-btn">
                + {t("Upload")}
              </button>
            )}
          </div>

          <div className="ebooks-grid">
            {ebooks.map((ebook, index) => (
              <div className="ebook-card" key={ebook.id}>
                <div className="ebook-image-container" style={{ backgroundColor: backgroundColors[index % backgroundColors.length] }}>
                  <img src="/images/ebooks/ebooks1.png" alt={ebook.title} className="ebook-image" />
                </div>
                <h5 className="ebook-title">
                  {i18n.language === "ar" ? ebook.titleAr : ebook.title}
                </h5>
                <p className="ebook-price">${ebook.price}</p>
                <div className="ebook-actions">
                  {hasAccess(ebook.id) ? (
                    <button onClick={() => handleView(ebook.id)} className="view-btn">{t("View")}</button>
                  ) : (
                    <button onClick={() => initiatePayment(ebook.id, ebook.price)} className="view-btn">
                      {t("BuyNow")} (${ebook.price})
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* PDF Viewer Modal */}
      <Modal
        isOpen={modalIsOpen}
        onRequestClose={() => setModalIsOpen(false)}
        className="pdf-modal"
        overlayClassName="pdf-overlay"
        ariaHideApp={false}
      >
        <div className="pdf-header">
          <button onClick={() => setModalIsOpen(false)} className="close-btn">
            ✖
          </button>
        </div>
        <div className="pdf-viewer">
          <Document file={pdfUrl} onLoadSuccess={onDocumentLoadSuccess}>
            <Page
              pageNumber={currentPage}
              width={Math.min(1200, window.innerWidth * 0.9)}
            />
          </Document>
          <div className="pdf-controls">
            <button disabled={currentPage <= 1} onClick={() => setCurrentPage(prev => prev - 1)}>⬅ {t("Previous")}</button>
            <span>{t("Page")} {currentPage} {t("of")} {numPages}</span>
            <button disabled={currentPage >= numPages} onClick={() => setCurrentPage(prev => prev + 1)}>{t("Next")} ➡</button>
          </div>
        </div>
      </Modal>

      {/* Upload Modal */}
      <Modal
        isOpen={uploadModalOpen}
        onRequestClose={() => setUploadModalOpen(false)}
        className="upload-modal"
        overlayClassName="pdf-overlay"
        ariaHideApp={false}
      >
        <div className="upload-header">
          <h3>{t("UploadNewEbook")}</h3>
          <button onClick={() => setUploadModalOpen(false)} className="close-btn">
            ✖
          </button>
        </div>
        <form className="upload-form" onSubmit={handleUpload}>
          <input
            type="text"
            placeholder={t("EbookTitle")}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
          />
          <textarea
            placeholder={t("EbookDescription")}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            required
            rows={3}
          />
          <input
            type="file"
            accept="application/pdf"
            onChange={(e) => setFile(e.target.files[0])}
            required
          />
          <button type="submit" disabled={uploading}>
            {uploading ? t("Uploading") : t("Upload")}
          </button>
        </form>
      </Modal>
    </div>
  );
};

export default Ebooks;
